/**
 * Copilot Warnings — Proactive Alerts & Anomaly Badges
 * ═══════════════════════════════════════════════════════════════
 * Non-intrusive alerts for detected issues and data anomalies.
 */
import { useCopilot } from './CopilotProvider';
import { AlertTriangle, X, TrendingDown, TrendingUp, Info } from 'lucide-react';

const SEV_STYLES = {
  high: { bg: 'bg-red-50 border-red-200', icon: 'text-red-500', text: 'text-red-700' },
  medium: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-500', text: 'text-amber-700' },
  low: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-500', text: 'text-blue-700' },
};

// ── 7. Proactive Warning Bar ─────────────────────────────────
export function CopilotWarningBar() {
  const { warnings, dismissWarning, enabled } = useCopilot();
  if (!enabled || !warnings.length) return null;

  return (
    <div className="px-6 pt-3 space-y-2">
      {warnings.map((w, i) => {
        const style = SEV_STYLES[w.severity] || SEV_STYLES.medium;
        return (
          <div key={i} className={`flex items-start gap-3 px-4 py-3 border rounded-xl ${style.bg} animate-in slide-in-from-top-2`}>
            <AlertTriangle size={16} className={`${style.icon} shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-semibold ${style.text}`}>{w.title}</div>
              <div className="text-[11px] text-gray-600 mt-0.5">{w.detail}</div>
              {w.action && <div className="text-[11px] text-gray-500 mt-1 italic">Suggested: {w.action}</div>}
            </div>
            <button onClick={() => dismissWarning(i)} className="p-0.5 rounded hover:bg-white/50 shrink-0">
              <X size={14} className="text-gray-400" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── 9. Anomaly Badge for data values ─────────────────────────
// Usage: <AnomalyBadge value={budget.spent} label="Budget Spent" expected={budget.planned} />
export function AnomalyBadge({ value, label, expected, threshold = 1.5 }) {
  const { enabled } = useCopilot();
  if (!enabled || !expected || !value) return null;

  const ratio = value / expected;
  if (ratio < threshold && ratio > (1 / threshold)) return null;

  const isOver = ratio > 1;
  const pct = Math.round((ratio - 1) * 100);

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ml-1 ${
      isOver ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
    }`} title={`${label}: ${isOver ? '+' : ''}${pct}% vs expected`}>
      {isOver ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {isOver ? '+' : ''}{pct}%
    </span>
  );
}

// ── 9. Anomaly Highlight Wrapper for table cells ─────────────
// Usage: <AnomalyCell value={val} expected={100} threshold={2}>{val}</AnomalyCell>
export function AnomalyCell({ value, expected, threshold = 2, children }) {
  const { enabled } = useCopilot();
  if (!enabled || !expected) return children;

  const ratio = value / (expected || 1);
  const isAnomaly = ratio > threshold || ratio < (1 / threshold);

  if (!isAnomaly) return children;
  return (
    <span className={`relative ${ratio > threshold ? 'text-red-600 font-semibold' : 'text-blue-600 font-semibold'}`}>
      {children}
      <span className="absolute -top-1 -right-3">
        <AlertTriangle size={8} className={ratio > threshold ? 'text-red-400' : 'text-blue-400'} />
      </span>
    </span>
  );
}
